import { PaymentOptionItem } from "@/common/lib/types";

export type BnplScenario = "oneService" | "samePlan" | "differentPlans" | "fallback";

export interface BnplPlanService {
  type: string;
  title?: string;
  logoUrl: string;
  totalInstallments: number;
  monthlyAmount: number;
}

export interface BnplPlanInfo {
  scenario: BnplScenario;
  services: BnplPlanService[];
  /** Shared installment count, set for the oneService and samePlan scenarios */
  months?: number;
}

const hasValidInstallmentCount = (item: PaymentOptionItem): boolean =>
  typeof item?.totalInstallments === "number" && Number.isFinite(item.totalInstallments) && item.totalInstallments >= 1;

export const getBnplPlanInfo = (
  installments: PaymentOptionItem[] | undefined,
  enabledTypes: { [type: string]: boolean },
  totalPrice: number,
): BnplPlanInfo => {
  const activeServices = (installments ?? []).filter(
    (item) =>
      item &&
      item.type !== "valu" &&
      item.enable !== false &&
      !!item.media?.url &&
      !!enabledTypes[item.type],
  );

  if (
    !activeServices.length ||
    !totalPrice ||
    totalPrice <= 0 ||
    !activeServices.every(hasValidInstallmentCount)
  ) {
    return { scenario: "fallback", services: [] };
  }

  const services: BnplPlanService[] = activeServices.map((item) => ({
    type: item.type,
    title: item.title,
    logoUrl: item.media?.url ?? "",
    totalInstallments: item.totalInstallments!,
    monthlyAmount: Math.round((totalPrice / item.totalInstallments!) * 100) / 100,
  }));

  if (services.length === 1) {
    return { scenario: "oneService", services, months: services[0].totalInstallments };
  }

  const allSamePlan = services.every(
    (service) => service.totalInstallments === services[0].totalInstallments,
  );
  if (allSamePlan) {
    return { scenario: "samePlan", services, months: services[0].totalInstallments };
  }

  return { scenario: "differentPlans", services };
};

/**
 * Replaces {{placeholder}} tokens in a CMS template. Returns empty string when the template is not set in Sanity.
 */
export const applyBnplTemplate = (
  template: string | undefined,
  values: Record<string, string | number>,
): string => {
  if (!template?.trim()) {
    return "";
  }
  return Object.entries(values).reduce(
    (message, [key, value]) => message.split(`{{${key}}}`).join(String(value)),
    template,
  );
};
